import React, { useEffect, useRef, useState } from 'react';
import apiClient from '../../../service/apiClient';
import { toast } from 'react-toastify';
import { isAxiosError } from 'axios';
import { ResponseError } from '../../../type/type';

/**
 * @author Claude
 * @date 2026.06.09
 * @description 카카오 채널 연동 단일 페이지.
 *  검색용 아이디 + 휴대폰 번호 입력 → 인증번호 요청 → 인증번호 입력 → 완료(발신프로필 생성).
 *  기존 온보딩 튜토리얼(steps/Step0~Step9)을 대체한다.
 */

// 기존 플로우에서 사용하던 고정 카테고리 코드
const FIXED_CATEGORY_CODE = '00700080001';

interface BizLinkFormProps {
  /** 연동(발신프로필 생성) 성공 시 호출. 보통 템플릿 승인 화면(step 10)으로 이동 */
  onSuccess: () => void;
  /** 가맹점 정보 재조회 (profileKey 갱신용) */
  getMyFranchise?: () => void;
}

const BizLinkForm: React.FC<BizLinkFormProps> = ({ onSuccess, getMyFranchise }) => {
  const [plusFriend, setPlusFriend] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [token, setToken] = useState('');

  const [codeVisible, setCodeVisible] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [isSending, setIsSending] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const startTimer = () => {
    setSecondsLeft(180);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setSecondsLeft(prev => {
        if (prev <= 1) {
          if (timerRef.current) clearInterval(timerRef.current);
          timerRef.current = null;
          setCodeVisible(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // 1단계: 인증번호 요청
  const requestCode = async () => {
    if (!plusFriend.trim()) {
      toast.warning('검색용 아이디를 입력해주세요.');
      return;
    }
    if (!phoneNumber) {
      toast.warning('휴대폰 번호를 입력해주세요.');
      return;
    }

    try {
      setIsSending(true);
      const { data } = await apiClient.post('/api/bizmsg/sender/token', {
        plusFriend: plusFriend.trim(),
        phoneNumber,
      });

      if (data?.code === 'success') {
        toast.success('인증번호가 카카오톡으로 전송되었습니다!');
        setCodeVisible(true);
        startTimer();
      } else {
        toast.error(data?.message || '인증번호 전송에 실패했습니다.');
      }
    } catch (e: any) {
      if (isAxiosError<ResponseError>(e)) {
        const msg = e.response?.data?.errorMsg || '인증번호 전송에 실패했습니다.';
        const detail = e.response?.data?.errorData;
        toast.error(detail ? `${msg}: ${detail}` : msg);
      } else {
        toast.error(e?.message || '인증번호 전송 중 오류가 발생했습니다.');
      }
    } finally {
      setIsSending(false);
    }
  };

  // 2단계: 인증번호 입력 + 완료 → 발신프로필 생성
  const submit = async () => {
    if (!plusFriend.trim() || !phoneNumber || !token) {
      toast.warning('검색용 아이디, 휴대폰 번호, 인증번호를 모두 입력해주세요.');
      return;
    }
    if (token.length < 4) {
      toast.warning('인증번호를 정확히 입력해주세요.');
      return;
    }

    try {
      setIsSubmitting(true);
      const { data } = await apiClient.post('/api/bizmsg/sender/create', {
        plusFriend: plusFriend.trim(),
        phoneNumber,
        token,
        categoryCode: FIXED_CATEGORY_CODE,
      });

      if (data?.code === 'success') {
        toast.success('카카오 채널 연동이 완료되었습니다!');
        if (timerRef.current) clearInterval(timerRef.current);
        if (getMyFranchise) await getMyFranchise();
        onSuccess();
      } else {
        toast.error(data?.message || '유효하지 않은 인증번호입니다.');
      }
    } catch (e: any) {
      if (isAxiosError(e)) {
        toast.error((e.response?.data as any)?.message || '유효하지 않은 인증번호입니다.');
      } else {
        toast.error(e?.message || '연동 중 오류가 발생했습니다.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    boxSizing: 'border-box',
    border: '1px solid #d1d5db',
    borderRadius: 10,
    padding: '14px 16px',
    fontSize: 15,
    color: '#1f2937',
    outline: 'none',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: 13,
    fontWeight: 700,
    color: '#374151',
    marginBottom: 8,
  };

  return (
    <div
      style={{
        minHeight: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        padding: '40px 16px',
        background: '#f9fafb',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 460,
          background: 'white',
          borderRadius: 16,
          boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
          border: '1px solid #f0f0f0',
          padding: '32px 28px',
        }}
      >
        {/* 헤더 */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>💬</div>
          <h2 style={{ fontSize: 22, fontWeight: 800, color: '#111827', margin: 0 }}>
            카카오 채널 연동
          </h2>
          <p style={{ fontSize: 14, color: '#6b7280', marginTop: 8, lineHeight: 1.5 }}>
            검색용 아이디와 채널 관리자 휴대폰 번호로
            <br />
            인증 후 바로 연동됩니다.
          </p>
        </div>

        {/* 검색용 아이디 */}
        <div style={{ marginBottom: 18 }}>
          <label style={labelStyle}>검색용 아이디</label>
          <input
            type="text"
            value={plusFriend}
            onChange={e => setPlusFriend(e.target.value)}
            placeholder="예: @사주톡톡 (채널 검색용 ID)"
            style={inputStyle}
            disabled={codeVisible}
          />
        </div>

        {/* 휴대폰 번호 + 인증번호 요청 */}
        <div style={{ marginBottom: 18 }}>
          <label style={labelStyle}>채널 관리자 휴대폰 번호</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              type="text"
              value={phoneNumber}
              onChange={e => {
                const numbersOnly = e.target.value.replace(/\D/g, '');
                if (numbersOnly.length <= 11) setPhoneNumber(numbersOnly);
              }}
              placeholder="01012345678"
              style={{ ...inputStyle, flex: 1 }}
            />
            <button
              onClick={requestCode}
              disabled={secondsLeft > 0 || isSending || !plusFriend.trim() || !phoneNumber}
              style={{
                whiteSpace: 'nowrap',
                padding: '0 18px',
                borderRadius: 10,
                border: 'none',
                fontSize: 14,
                fontWeight: 700,
                color: 'white',
                cursor:
                  secondsLeft > 0 || isSending || !plusFriend.trim() || !phoneNumber
                    ? 'not-allowed'
                    : 'pointer',
                background:
                  secondsLeft > 0 || isSending || !plusFriend.trim() || !phoneNumber
                    ? '#cbd5e1'
                    : '#3b82f6',
                minWidth: 96,
              }}
            >
              {isSending
                ? '전송 중...'
                : secondsLeft > 0
                ? formatTime(secondsLeft)
                : codeVisible
                ? '재전송'
                : '인증번호 요청'}
            </button>
          </div>
        </div>

        {/* 인증번호 입력 (요청 후에만 노출) */}
        {codeVisible && (
          <div style={{ marginBottom: 24 }}>
            <label style={labelStyle}>인증번호</label>
            <input
              type="text"
              value={token}
              onChange={e => {
                const cleaned = e.target.value.replace(/\s/g, '');
                if (cleaned.length <= 6) setToken(cleaned);
              }}
              placeholder="카카오톡으로 전송된 인증번호"
              style={inputStyle}
              onKeyDown={e => {
                if (e.key === 'Enter') submit();
              }}
            />
          </div>
        )}

        {/* 완료 버튼 */}
        <button
          onClick={submit}
          disabled={!codeVisible || !token || isSubmitting}
          style={{
            width: '100%',
            padding: '16px',
            borderRadius: 12,
            border: 'none',
            fontSize: 16,
            fontWeight: 800,
            color: 'white',
            cursor: !codeVisible || !token || isSubmitting ? 'not-allowed' : 'pointer',
            background: !codeVisible || !token || isSubmitting ? '#cbd5e1' : '#22c55e',
            transition: 'background 0.2s ease',
          }}
        >
          {isSubmitting ? '연동 중...' : '완료'}
        </button>
      </div>
    </div>
  );
};

export default BizLinkForm;
